import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import '../styles/Legal.css';

const Legal = () => {
  const { type } = useParams();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
    window.scrollTo(0, 0);
  }, [type]);

  const content = {
    privacy: {
      title: 'Privacy Policy',
      lastUpdated: 'March 1, 2025',
      sections: [
        {
          heading: 'Introduction',
          content: 'At Madeira Outdoors, we are committed to protecting your privacy. This Privacy Policy explains how we collect, use, and safeguard your personal information when you use our services or visit our website.'
        },
        {
          heading: 'Information We Collect',
          content: 'We collect information that you provide directly to us, including your name, email address, phone number, and booking details. We also collect information about your use of our website through cookies and similar technologies.'
        },
        {
          heading: 'How We Use Your Information',
          content: 'We use the information we collect to provide and improve our services, process bookings, communicate with you about your tours, send you marketing communications (with your consent), and comply with legal obligations.'
        },
        {
          heading: 'Information Sharing',
          content: 'We do not sell your personal information. We may share your information with service providers who assist us in operating our business, and when required by law or to protect our rights.'
        },
        {
          heading: 'Data Security',
          content: 'We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction.'
        },
        {
          heading: 'Your Rights',
          content: 'You have the right to access, correct, or delete your personal information. You may also object to processing or request data portability. To exercise these rights, please contact us at bookings@madeiraoutdoors.com.'
        },
        {
          heading: 'Contact Us',
          content: 'If you have any questions about this Privacy Policy, please contact us at bookings@madeiraoutdoors.com or +351 291 000 000.'
        }
      ]
    },
    terms: {
      title: 'Terms and Conditions',
      lastUpdated: 'March 1, 2025',
      sections: [
        {
          heading: 'Acceptance of Terms',
          content: 'By using our services, you agree to be bound by these Terms and Conditions. If you do not agree with any part of these terms, you may not use our services.'
        },
        {
          heading: 'Booking and Payment',
          content: 'All bookings are subject to availability. Payment must be made according to the terms specified at the time of booking. Cancellation policies vary by tour and will be clearly communicated during the booking process.'
        },
        {
          heading: 'Your Responsibilities',
          content: 'You are responsible for ensuring you have the necessary fitness level and health condition for your chosen activity. You must follow the instructions of our guides and respect safety guidelines at all times.'
        },
        {
          heading: 'Liability',
          content: 'While we take all reasonable precautions to ensure your safety, outdoor activities involve inherent risks. Participants engage in activities at their own risk. Our liability is limited as specified in our insurance terms.'
        },
        {
          heading: 'Intellectual Property',
          content: 'All content on our website, including text, images, logos, and designs, is the property of Madeira Outdoors and protected by intellectual property laws.'
        },
        {
          heading: 'Changes to Terms',
          content: 'We reserve the right to modify these terms at any time. Continued use of our services after changes constitutes acceptance of the modified terms.'
        },
        {
          heading: 'Governing Law',
          content: 'These terms are governed by the laws of Portugal. Any disputes will be subject to the exclusive jurisdiction of the courts of Portugal.'
        }
      ]
    },
    cookies: {
      title: 'Cookie Policy',
      lastUpdated: 'March 1, 2025',
      sections: [
        {
          heading: 'What Are Cookies',
          content: 'Cookies are small text files that are placed on your device when you visit our website. They help us provide you with a better experience by remembering your preferences and understanding how you use our site.'
        },
        {
          heading: 'Types of Cookies We Use',
          content: 'We use essential cookies (necessary for the website to function), functional cookies (to remember your preferences), analytics cookies (to understand how you use our site), and marketing cookies (to show you relevant advertisements).'
        },
        {
          heading: 'Essential Cookies',
          content: 'These cookies are necessary for the website to function properly. They enable basic features like page navigation and access to secure areas. The website cannot function properly without these cookies.'
        },
        {
          heading: 'Analytics Cookies',
          content: 'We use analytics cookies to understand how visitors interact with our website. This helps us improve our website and services. These cookies collect information anonymously.'
        },
        {
          heading: 'Marketing Cookies',
          content: 'These cookies are used to track visitors across websites and display ads that are relevant and engaging. They may be set by us or by third-party advertising partners.'
        },
        {
          heading: 'Managing Cookies',
          content: 'You can control and manage cookies through your browser settings. Please note that removing or blocking cookies may impact your user experience and some features may not function properly.'
        },
        {
          heading: 'Updates to This Policy',
          content: 'We may update this Cookie Policy from time to time. The updated version will be indicated by an updated "Last Updated" date at the top of this policy.'
        }
      ]
    }
  };

  const pageContent = content[type] || content.privacy;

  return (
    <div className="legal-page">
      {/* Page Header */}
      <section className="page-hero">
        <div className="page-hero-overlay"></div>
        <div className="container">
          <div className={`page-hero-content ${isVisible ? 'animated fadeIn' : 'animated'}`}>
            <h1 className="display-large">{pageContent.title}</h1>
            <p className="body-large">Last Updated: {pageContent.lastUpdated}</p>
          </div>
        </div>
      </section>

      {/* Legal Content */}
      <section className="legal-content-section">
        <div className="container">
          <div className="legal-content">
            {pageContent.sections.map((section, index) => (
              <div key={index} className="legal-section">
                <h2 className="heading-2">{section.heading}</h2>
                <p className="body-medium">{section.content}</p>
              </div>
            ))}

            <div className="legal-footer">
              <p className="body-medium">
                For any questions regarding this {pageContent.title.toLowerCase()}, please contact us at:
              </p>
              <div className="contact-details">
                <p><strong>Email:</strong> bookings@madeiraoutdoors.com</p>
                <p><strong>Phone:</strong> +351 291 000 000</p>
                <p><strong>Address:</strong> Rua da Levada 123, Funchal, 9000-123, Madeira Island, Portugal</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Legal;
